import React from 'react';
import ReactDOM from 'react-dom/client';
import { FluentProvider, webLightTheme } from '@fluentui/react-components';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { MsalProvider } from '@azure/msal-react';
import { createMsalInstance } from './config/msal';
import { AuthRequiredError } from './utils/errors';
import App from './App';
import './App.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) =>
        !(error instanceof AuthRequiredError) && failureCount < 3,
    },
  },
});

function renderAuthenticationError(err: unknown) {
  const root = document.getElementById('root');
  if (root) {
    const container = document.createElement('div');
    container.style.cssText = 'padding:2rem;font-family:sans-serif';
    const h1 = document.createElement('h1');
    h1.textContent = 'Authentication Error';
    const p = document.createElement('p');
    p.textContent = 'Failed to initialize authentication. Please refresh the page or contact support.';
    const pre = document.createElement('pre');
    pre.style.color = '#a4262c';
    pre.textContent = String(err);
    container.append(h1, p, pre);
    root.appendChild(container);
  }
  console.error('MSAL initialization failed:', err);
}

try {
  const msalInstance = createMsalInstance();

  msalInstance.initialize().then(async () => {
    // Process any redirect response before rendering.
    // If the redirect URI changed (e.g., /redirect.html -> origin), users may
    // have stale MSAL interaction state that causes no_token_request_cache_error.
    // We clear the hash and let the user sign in fresh rather than crashing.
    try {
      await msalInstance.handleRedirectPromise({ navigateToLoginRequestUrl: false });
    } catch (err: unknown) {
      const msalErr = err as { errorCode?: string };
      if (msalErr?.errorCode === 'no_token_request_cache_error') {
        console.warn('Stale redirect state cleared:', err);
      } else {
        throw err;
      }
      // Clear the stale auth code hash so the page loads cleanly
      if (window.location.hash.includes('code=')) {
        window.history.replaceState(null, '', window.location.pathname);
      }
    }

    ReactDOM.createRoot(document.getElementById('root')!).render(
      <React.StrictMode>
        <MsalProvider instance={msalInstance}>
          <QueryClientProvider client={queryClient}>
            <FluentProvider theme={webLightTheme}>
              <App />
            </FluentProvider>
          </QueryClientProvider>
        </MsalProvider>
      </React.StrictMode>,
    );
  }).catch(renderAuthenticationError);
} catch (err) {
  renderAuthenticationError(err);
}
